import React from 'react';
import _ from 'lodash';

import Joi from 'joi';
import JoiPhoneNumber from 'joi-phone-number';

import AutoComplete from '../themed/autocomplete/autocomplete';
import Conditional from '../common/conditional';
import Form from '../common/form';
import Progress from '../themed/progress';

import withCountries from '../hoc/withCountries';

import * as service from '../../services/organizationService';
import * as geocodeService from '../../services/geocode';
import * as toastService from '../../services/toastService';

import * as models from '../../utils/models';
import * as utils from '../../utils/utilities';

class CreateOrganization extends Form {
	state = {
		data: {
			organisationName: '',
			organisationWebsite: '',
			organisationLogo: [],
			organisationEmail: '',
			organisationPhone: '',
			street: '',
			city: '',
			state: '',
			country: '',
		},
		errors: {},
		loading: false,
		geocoding: false,
		progress: 0,
		places: [],
	};

	schema = {
		organisationName: Joi.string().min(3).required().label('Organization Name'),
		organisationWebsite: Joi.string()
			.min(3)
			.required()
			.label('Organization Url'),
		organisationEmail: Joi.string()
			.email({ tlds: false })
			.required()
			.label('Contact Email'),
		organisationPhone: Joi.extend(JoiPhoneNumber)
			.string()
			.phoneNumber({ defaultCountry: 'NG', strict: true })
			.label('Contact Phone number'),
		organisationLogo: Joi.array().min(1).required().label('Organization Logo'),
		street: Joi.string().min(3).required().label('Street'),
		city: Joi.string().min(3).required().label('City'),
		state: Joi.string().min(3).required().label('State'),
		country: Joi.string().min(3).required().label('Country'),
	};

	handleStateChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
		await this.handleChange(e);

		const { country, state } = this.state.data;
		if (!country || !state) return;

		this.props.getCitiesInState(country, state);
	};

	handleCountryChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
		await this.handleChange(e);

		this.props.getStatesInCountry(this.state.data.country);
	};

	getOrganizationLogo = (): string => {
		const logo = this.state.data.organisationLogo[0] as models.PDFImageProps;
		return logo.src;
	};

	getApiData = () => {
		const { data } = this.state;
		const apiData: any = {};

		apiData.organisationEmail = data.organisationEmail;
		apiData.organisationPhone = data.organisationPhone;
		apiData.organisationName = data.organisationName;
		apiData.organisationWebsite = data.organisationWebsite;
		apiData.organisationLogo = this.getOrganizationLogo();
		apiData.organisationAddress = _.pick(data, [
			'street',
			'city',
			'state',
			'country',
		]);

		return apiData;
	};

	handleSetAddress = (address: string) => {
		const data = { ...this.state.data };
		data.street = address;

		this.setState({ data, places: [] } as any);
	};

	handleAddressChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		this.handleChange(e);

		const error: string | null = this.validateProperty(e);
		if (error) return;

		try {
			this.setState({ geocoding: true } as any);

			const { data } = await geocodeService.getPlaces(e.target.value);
			const places = data.results.map(utils.transformPlaceSearchResult);

			this.setState({ places, geocoding: false } as any);
		} catch (error: any) {
			this.setState({ geocoding: false } as any);
		}
	};

	handleAddressAutocompleteClose = () => {
		this.setState({ places: [] } as any);
	};

	postData = async (): Promise<void> => {
		const apiData = this.getApiData();

		await service.createOrganization(apiData, this.handleProgressChange);
		window.location.reload();
	};

	doSubmit = (): void => {
		toastService.promise(this.postData(), {
			loading: 'Creating Organization',
			success: 'Organization Created',
			error: utils.getErrorMessage,
		});
	};

	render() {
		const { geocoding, places } = this.state;

		return (
			<React.Fragment>
				<Progress progress={this.state.progress} />

				<form autoComplete="off" onSubmit={this.handleSubmit} className="p-3">
					<h1 className="form-title">Create a New Organization</h1>

					<div className="divider my-4"></div>

					{this.renderFileInput({
						max: 1,
						multiple: false,
						name: 'organisationLogo',
						accept: 'image/*,application/pdf',
						label: 'Organization Logo',
						sublabel: 'Upload Organization Logo',
						types: utils.getFileUploadTypes(),
					})}

					<div className="divider my-4"></div>

					<div className="row mt-2">
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'organisationName',
								label: 'Organization Name',
								placeholder: 'e.g. ExxonMobil',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'url',
								name: 'organisationWebsite',
								label: 'Organisation Url',
								placeholder: 'e.g. https://russelsmithgroup.com/',
							})}
						</div>
					</div>

					<div className="divider my-4"></div>

					<div>
						{this.renderInput({
							type: 'text',
							name: 'street',
							label: 'Street Address',
							placeholder: 'Enter Street Address',
							onChange: this.handleAddressChange,
						})}

						<Conditional isVisible={places.length > 0}>
							<AutoComplete
								geocoding={geocoding}
								data={places}
								onSelect={this.handleSetAddress}
								onCancel={this.handleAddressAutocompleteClose}
							/>
						</Conditional>
					</div>

					<div className="divider my-4"></div>

					<div className="row mt-2">
						<div className="col">
							{this.renderSelect({
								name: 'country',
								label: 'Country',
								placeholder: 'Select Country',
								options: this.props.countries,
								onChange: this.handleCountryChange,
							})}
						</div>
						<div className="col">
							{this.renderSelect({
								name: 'state',
								label: 'State',
								placeholder: 'Select State',
								options: this.props.states,
								onChange: this.handleStateChange,
							})}
						</div>
						<div className="col">
							{this.renderSelect({
								name: 'city',
								label: 'City',
								placeholder: 'Select City',
								options: this.props.cities,
							})}
						</div>
					</div>

					<div className="divider my-4"></div>

					<div className="row mt-2">
						<div className="col">
							{this.renderInput({
								type: 'email',
								name: 'organisationEmail',
								label: 'Email Address',
								placeholder: 'e.g. jdoe@russelsmithgroup.com',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'number',
								name: 'organisationPhone',
								label: 'Phone Number',
								placeholder: 'e.g. 08142317489',
							})}
						</div>
					</div>

					<div className="d-flex justify-content-end">
						{this.renderButton('Create')}
					</div>
				</form>
			</React.Fragment>
		);
	}
}

export default withCountries(CreateOrganization);
